import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { API_BASE } from '../services/api';

interface SearchResult {
  id: string;
  type: 'politician' | 'party' | 'initiative';
  name: string;
  subtitle: string;
}

export default function SearchBar() {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<SearchResult[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const navigate = useNavigate();

  const handleSearch = async (value: string) => {
    setQuery(value);

    if (value.length >= 2) {
      try {
        const response = await fetch(`${API_BASE}/search?q=${encodeURIComponent(value)}`);
        const data = await response.json();
        setResults(data.data || []);
        setIsOpen(true);
      } catch (error) {
        console.error('Search error:', error);
      }
    } else {
      setResults([]);
      setIsOpen(false);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (query.trim()) {
      navigate(`/buscar?q=${encodeURIComponent(query)}`);
      setIsOpen(false);
    }
  };

  const handleSelect = (result: SearchResult) => {
    if (result.type === 'politician') {
      navigate(`/politico/${result.id}`);
    } else if (result.type === 'party') {
      navigate(`/partido/${result.id}`);
    }
    setIsOpen(false);
    setQuery('');
  };

  return (
    <div className="relative">
      <form onSubmit={handleSubmit} className="relative">
        <input
          type="text"
          value={query}
          onChange={(e) => handleSearch(e.target.value)}
          onFocus={() => results.length > 0 && setIsOpen(true)}
          placeholder="Buscar políticos, partidos, iniciativas..."
          className="input pr-10"
          autoComplete="off"
        />
        <button
          type="submit"
          className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
        </button>
      </form>

      {/* Dropdown Results */}
      {isOpen && results.length > 0 && (
        <div className="absolute top-full left-0 right-0 mt-2 bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden z-50">
          <ul className="max-h-96 overflow-auto">
            {results.slice(0, 8).map((result) => (
              <li
                key={`${result.type}-${result.id}`}
                onClick={() => handleSelect(result)}
                className="px-4 py-3 hover:bg-gray-50 cursor-pointer transition-colors"
              >
                <div className="flex items-center space-x-3">
                  <div className={`w-2 h-2 rounded-full ${
                    result.type === 'politician' ? 'bg-blue-500' :
                    result.type === 'party' ? 'bg-green-500' : 'bg-purple-500'
                  }`} />
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-gray-900 truncate">{result.name}</p>
                    <p className="text-sm text-gray-500 truncate">{result.subtitle}</p>
                  </div>
                </div>
              </li>
            ))}
          </ul>
          <div className="px-4 py-2 bg-gray-50 border-t border-gray-200">
            <button
              onClick={() => navigate(`/buscar?q=${encodeURIComponent(query)}`)}
              className="text-sm text-primary-600 hover:text-primary-700 font-medium"
            >
              Ver todos los resultados →
            </button>
          </div>
        </div>
      )}

      {/* Click outside to close */}
      {isOpen && (
        <div
          className="fixed inset-0 z-[-1]"
          onClick={() => setIsOpen(false)}
        />
      )}
    </div>
  );
}
